import {
  hireSourceParams,
  withHireSourcing,
} from '@/routes/Agency/HireSourcing/hireSourcing';
import { axiosRubyClient } from '@/util/axios/axiosClient';

import { schemas } from './generated/schemas';
import {
  CalendarWeekData,
  DashboardFilterValues,
  DEFAULT_QUEUE_PAGE,
  EMPTY_QUEUE_FILTERS,
  FilterOptions,
  MissingTimesheetData,
  NeedsApprovalData,
  NoShowData,
  QueuePage,
  QueueFilterValues,
  queueFilterParams,
  queuePageParams,
  ActivityOrder,
  ShiftActivityData,
  DashboardAlertsData,
  ShiftDetailData,
  ShiftHandlingResult,
  SummaryCounts,
  TimesheetReview,
  TimesheetReviewEdits,
} from './types';

// REST client for /api/admin/dashboard_v2/*. Each call pipes the response
// through the matching generated Zod schema's `.parse()` so the UI fails fast
// on contract drift with the Rails endpoints. No GraphQL.
export const dashboardV2Client = {
  async getCalendarWeek(params: {
    agencyId: number;
    startDate: string;
    timeZone: string;
    filters: DashboardFilterValues;
    /** Window span: 7 for the week grid, 1 for the Day view (GRAVY-10329). */
    days?: number;
  }): Promise<CalendarWeekData> {
    const { agencyId, startDate, timeZone, filters, days = 7 } = params;
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/calendar_week',
      {
        params: {
          agency_id: agencyId,
          start_date: startDate,
          days,
          time_zone: timeZone,
          customer_ids: filters.customerIds,
          account_ids: filters.accountIds,
          skill_ids: filters.skillIds,
          region_ids: filters.regionIds,
          status: filters.status,
          job_type: filters.jobType,
          fill_status: filters.fillStatus,
          fill_reason: filters.fillReason,
        },
        // `indexes: false` emits customer_ids[]=1&customer_ids[]=2, which Rails
        // parses as an array — the same serialization the three queue calls
        // below use.
        //
        // This was `indexes: null`, which emits BARE repeated keys
        // (customer_ids=1&customer_ids=2). Rack collapses those to the LAST
        // value, so every calendar filter silently applied only the last
        // selection: picking two clients filtered by one. The comment already
        // described the `[]` form — only the option was wrong.
        paramsSerializer: { indexes: false },
      },
    );
    return schemas.calendar_week_response.parse(data).data;
  },

  async getSummary(agencyId: number): Promise<SummaryCounts> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/summary',
      { params: { agency_id: agencyId } },
    );
    return schemas.summary_response.parse(data).data;
  },

  async getNoShow(
    agencyId: number,
    filters: QueueFilterValues = EMPTY_QUEUE_FILTERS,
    page: QueuePage = DEFAULT_QUEUE_PAGE,
  ): Promise<NoShowData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/no_show',
      {
        params: {
          agency_id: agencyId,
          ...queueFilterParams(filters),
          ...queuePageParams(page),
        },
        // `indexes: false` emits customer_ids[]=1&customer_ids[]=2, which Rails
        // parses as an array. The default emits customer_ids[0]=… and
        // `indexes: null` emits bare repeated keys, which Rails collapses to
        // the LAST value — silently dropping every selection but one.
        paramsSerializer: { indexes: false },
      },
    );
    return schemas.no_show_response.parse(data).data;
  },

  async getMissingTimesheet(
    agencyId: number,
    filters: QueueFilterValues = EMPTY_QUEUE_FILTERS,
    page: QueuePage = DEFAULT_QUEUE_PAGE,
  ): Promise<MissingTimesheetData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/missing_timesheet',
      {
        params: {
          agency_id: agencyId,
          ...queueFilterParams(filters),
          ...queuePageParams(page),
        },
        // `indexes: false` emits customer_ids[]=1&customer_ids[]=2, which Rails
        // parses as an array. The default emits customer_ids[0]=… and
        // `indexes: null` emits bare repeated keys, which Rails collapses to
        // the LAST value — silently dropping every selection but one.
        paramsSerializer: { indexes: false },
      },
    );
    return schemas.missing_timesheet_response.parse(data).data;
  },

  // GRAVY-10366 — the per-worker Review modal.
  async getTimesheetReview(
    agencyId: number,
    timesheetId: number,
  ): Promise<TimesheetReview> {
    const { data } = await axiosRubyClient.get(
      `/api/admin/dashboard_v2/timesheets/${timesheetId}/review`,
      { params: { agency_id: agencyId } },
    );
    return schemas.timesheet_review_response.parse(data).data;
  },

  // Approve / Reject return the RE-RENDERED review payload, so the caller
  // repaints from the server's answer rather than guessing what changed.
  async approveTimesheet(
    agencyId: number,
    timesheetId: number,
    edits: TimesheetReviewEdits = {},
  ): Promise<TimesheetReview> {
    const { data } = await axiosRubyClient.post(
      `/api/admin/dashboard_v2/timesheets/${timesheetId}/approve`,
      edits,
      { params: { agency_id: agencyId } },
    );
    return schemas.timesheet_review_response.parse(data).data;
  },

  async rejectTimesheet(
    agencyId: number,
    timesheetId: number,
  ): Promise<TimesheetReview> {
    const { data } = await axiosRubyClient.post(
      `/api/admin/dashboard_v2/timesheets/${timesheetId}/reject`,
      {},
      { params: { agency_id: agencyId } },
    );
    return schemas.timesheet_review_response.parse(data).data;
  },

  async getNeedsApproval(
    agencyId: number,
    filters: QueueFilterValues = EMPTY_QUEUE_FILTERS,
    page: QueuePage = DEFAULT_QUEUE_PAGE,
  ): Promise<NeedsApprovalData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/needs_approval',
      {
        params: {
          agency_id: agencyId,
          ...queueFilterParams(filters),
          ...queuePageParams(page),
        },
        // `indexes: false` emits customer_ids[]=1&customer_ids[]=2, which Rails
        // parses as an array. The default emits customer_ids[0]=… and
        // `indexes: null` emits bare repeated keys, which Rails collapses to
        // the LAST value — silently dropping every selection but one.
        paramsSerializer: { indexes: false },
      },
    );
    return schemas.needs_approval_response.parse(data).data;
  },

  async getShiftDetail(
    agencyId: number,
    shiftId: number,
  ): Promise<ShiftDetailData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/shift_detail',
      { params: { agency_id: agencyId, shift_id: shiftId } },
    );
    return schemas.shift_detail_response.parse(data).data;
  },

  // GRAVY-10443 Take Over. Both return the panel's NEW handling state, so the
  // caller repaints from the server's answer. A 409 carries the holder's name
  // in `errors[0]` ("Peter B. is handling this shift").
  async takeOverShift(
    agencyId: number,
    shiftId: number,
  ): Promise<ShiftHandlingResult> {
    const { data } = await axiosRubyClient.post(
      `/api/admin/dashboard_v2/shifts/${shiftId}/take_over`,
      {},
      { params: { agency_id: agencyId } },
    );
    return schemas.shift_handling_response.parse(data).data;
  },

  async resolveShift(
    agencyId: number,
    shiftId: number,
  ): Promise<ShiftHandlingResult> {
    const { data } = await axiosRubyClient.post(
      `/api/admin/dashboard_v2/shifts/${shiftId}/resolve`,
      {},
      { params: { agency_id: agencyId } },
    );
    return schemas.shift_handling_response.parse(data).data;
  },

  // The toast feed. Without `afterId` the server returns only the cursor, so a
  // dashboard opened now never replays alerts raised before it was opened.
  async getAlerts(
    agencyId: number,
    afterId: number | null,
  ): Promise<DashboardAlertsData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/alerts',
      {
        params: {
          agency_id: agencyId,
          ...(afterId != null ? { after_id: afterId } : {}),
        },
      },
    );
    return schemas.dashboard_alerts_response.parse(data).data;
  },

  // Full activity history for one shift, behind the panel's "View all"
  // (GRAVY-10294). Deliberately a separate call from getShiftDetail: the panel
  // only ever needs its preview, so the whole history is fetched on demand and
  // paginated rather than loaded on every panel open.
  //
  // `order` is the Job page Activity tab's sort toggle (GRAVY-10877). It is
  // the SERVER's order, not a client re-sort: reversing one newest-first page
  // would show the newest rows backwards, never the oldest ones.
  async getShiftActivity(
    agencyId: number,
    shiftId: number,
    page: number,
    perPage: number,
    order: ActivityOrder = 'desc',
  ): Promise<ShiftActivityData> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/shift_activity',
      {
        params: {
          agency_id: agencyId,
          shift_id: shiftId,
          page,
          per_page: perPage,
          order,
        },
      },
    );
    return schemas.shift_activity_response.parse(data).data;
  },

  /**
   * Hire a worker onto a job (GRAVY-10350 C).
   *
   * The AUTHORITATIVE hire endpoint — the same `Jobs::HireWorkerService` the
   * Job page drives, so eligibility, withholdings, overlap and the
   * customer-admin past-job rule are enforced once and identically. Dashboard
   * V2 adds no hire logic of its own.
   *
   * Deliberately un-parsed: the response is only checked for success, and the
   * caller refetches the shift rather than trusting a returned shape.
   */
  async hireWorker(jobId: number, workerId: number): Promise<void> {
    // GRAVY-10443: on a flagged shift the admin is asked how they found the
    // worker, and the hire retried with the answer.
    await withHireSourcing((source) =>
      axiosRubyClient.post(`/api/admin/jobs/${jobId}/hire_worker`, {
        worker_id: workerId,
        // Preferred Pools & Dependability v2 analytics (2026-09-02 plan):
        // Dashboard V2 is its own staffing surface.
        staffing_tab: 'dashboard_v2',
        ...hireSourceParams(source),
      }),
    );
  },

  async getFilters(agencyId: number): Promise<FilterOptions> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/dashboard_v2/filters',
      { params: { agency_id: agencyId } },
    );
    return schemas.filters_response.parse(data).data;
  },
};
